import VerifiedIcon from "@mui/icons-material/Verified";
import { Box } from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridSortItem,
} from "@mui/x-data-grid";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex from "../../../../../components/Flex";
import PageLayout from "../../../../../components/PageLayout";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import StatusChip from "../../../../../components/StatusChip";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../components/app/DeleteModal";
import { UserData } from "../../../../../dummy-data/user-data";
import { useDebounce } from "../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useArchiveState } from "../../../../../hooks/useArchiveState";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAcceptReferralPatient } from "../../../../../queries/collaboration/referral/use-accept-referral-patient";
import { useProvidersReferralList } from "../../../../../queries/collaboration/referral/use-get-providers-referral-list";
import { useGetReferralById } from "../../../../../queries/collaboration/referral/use-get-referral-by-uuid";
import { useListReferral } from "../../../../../queries/collaboration/referral/use-list-referral";
import { useArchiveReferral } from "../../../../../queries/collaboration/referral/use-referral-archive";
import { useArchiveReferralProvider } from "../../../../../queries/collaboration/referral/use-referral-provider-archive";
import { AcceptReferralReferralStatusEnum } from "../../../../../services";
import { NonNullableDate } from "../../../../../utils/date-time-utility";
import {
  PatientContact,
  PatientName,
} from "../../../../../utils/util-components/UtilComponents";
import {
  DEFAULT_VALUE_LABEL_LIST,
  DEF_PAGINATION,
  applyDefaultColumnSettings,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  // hasActiveFilters,
  replaceUnderScoreAddSpace,
  transformMultiSelectFrontEndVal,
} from "../../../../../utils/utils";
import UploadFaxDocumentDrawer from "../../communications/fax/UploadFaxDocumentDrawer";
import { DOCUMENT_INITIAL_VALUE } from "../../communications/fax/fax-document-constant";
import NewAppointment from "../../scheduling/appointment/NewAppointment";
import {
  DEFAULT_FILTERS,
  PRIORITY_MAPPING,
  REFERRAL_FILTER_INITIAL_EXPANDED,
  ReferralFilters,
  getObjectFromList,
} from "../collaboration-referral-constant";
import ChangeReferralStatus from "./ChangeReferralStatus";
import NewReferringProvider from "./NewReferringProvider";
import ReferralTopBar from "./ReferralTopBar";
import UpsertReferral from "./UpsertReferral";
import ViewReferral from "./ViewReferral";

interface ReferralsProps {
  patientData?: any;
}

const Referrals = ({ patientData }: ReferralsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [
    newReferringProvider,
    appointmentDisclosure,
    changeReferralStatusDisclosureHost,
    createFaxClaimDisc,
  ] = useReduceDisclosure(4);
  const activeReferral = searchParams.get("activeReferral") || "referral-out";
  const [activeTab, setActiveTab] = useState(activeReferral);
  const [archive, setArchive] = useState(false);
  const [isReferral_Provider_list, setIsReferral_Provider_list] =
    useState<boolean>(activeReferral === "referral-provider-list");
  const { mutateAsync: archiveMutation } = useArchiveReferral();
  const { mutateAsync: archiveReferralProvider } = useArchiveReferralProvider();
  const { mutateAsync: acceptReferral } = useAcceptReferralPatient();
  const showSnackbar = useSnackbar();
  const { deleteDisc, archiveHandler } = useArchiveState(
    isReferral_Provider_list ? archiveReferralProvider : archiveMutation
  );

  const [searchStringKey, setSearchStringKey, instantSearchVal] =
    useDebounce<string>("");
  const [pagination, setPagination] =
    useState<GridPaginationModel>(DEF_PAGINATION);
  const [referralFilter, setReferralFilter] =
    useState<ReferralFilters>(DEFAULT_FILTERS);
  const [providerGridSorting, setProviderGridSorting] = useState<
    GridSortItem[]
  >([]);
  const {
    data: providersList,
    isFetching: isFetchingProvidersList,
    refetch: refetchProvidersList,
  } = useProvidersReferralList({
    enabled: activeTab === "referral-provider-list",
    pagination: pagination,
    gridSorting: providerGridSorting,
    xTENANTID: getXtenantId(),
    isReferral: true,
    searchKey:
      activeTab === "referral-provider-list" && searchStringKey
        ? searchStringKey
        : undefined,
    archive: archive,
    // enabled: activeTab === "referral-provider-list", // Only fetch when on provider tab
    // activeTab: activeTab, // Include activeTab to trigger refetch on tab change
  });

  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    REFERRAL_FILTER_INITIAL_EXPANDED
  );
  const isReferralOutTask = useQueryParams("activeReferral") === "referral-in";
  const [referralGridSorting, setReferralGridSorting] = useState<
    GridSortItem[]
  >([]);
  const {
    data: referral,
    isFetching,
    refetch,
  } = useListReferral({
    pagination: pagination,
    active: true,
    referralDate: referralFilter.date
      ? new Date(referralFilter.date).toISOString()
      : undefined,
    referralFrom: referralFilter.referral_from
      ? Object.keys(referralFilter.referral_from)[0]
      : undefined,
    referralTo: referralFilter.referral_to
      ? Object.keys(referralFilter.referral_to)[0]
      : undefined,
    referralSpeciality: referralFilter.speciality
      ? Object.keys(referralFilter.speciality)[0]
      : undefined,
    gridSorting: referralGridSorting,
    referralIn: isReferralOutTask,
    archive: archive,
    enabled: activeTab !== "referral-provider-list",
    // referralStatus: referralFilter.status as GetAllReferralReferralStatusEnum,
    searchString:
      activeTab !== "referral-provider-list" && searchStringKey
        ? searchStringKey
        : undefined,
    xTENANTID: getXtenantId(),
    patientId: patientData?.value ?? undefined,
  });

  const [referralId, setReferralId] = useState("");
  const { data } = useGetReferralById({
    referralId: referralId,
    xTENANTID: getXtenantId() as string,
  });

  const referralData = data?.data;

  const viewReferralDisclosureHost = useDisclosure();
  const referralDisclosureHost = useDisclosure(false, {
    onClose: () => {
      setReferralId("");
    },
  });

  //
  const handleAcceptReferral = (
    row: any,
    status: AcceptReferralReferralStatusEnum
  ) => {
    acceptReferral({
      patientId: row?.patientUuid ?? "",
      referralUuid: row?.uuid ?? "",
      referralStatus: status,
      xTENANTID: getXtenantId() ?? "",
    })
      .then((res: any) => {
        showSnackbar(getSuccessSnackbar(res));
        refetch();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const COLUMNS = applyDefaultColumnSettings(
    [
      {
        field: "patientName",
        headerName: "Patient Name",
        minWidth: 150,
        flex: 0.1,
        renderCell: ({ row }) => (
          <PatientName
            disableNavigate
            name={row?.patientName ?? ""}
            gender={row?.patientGender ?? ""}
            patientId={Object.keys(row?.patient ?? {})[0] ?? ""}
            birthDate={row?.patientDob ?? ""}
          />
        ),
      },
      {
        field: "contact",
        minWidth: 120,
        flex: 0.1,
        disableColumnMenu: true,
        headerName: "Contact",
        renderCell: ({ row }) => {
          return (
            <PatientContact
              contactNo={row?.patientContact}
              email={row?.patientEmail}
            />
          );
        },
      },
      {
        field: "referralFrom",
        headerName: "Referral From",
        minWidth: 120,
        flex: 0.12,
        renderCell: ({ row }) => {
          return (
            <Flex
              gap={"10px"}
              sx={{
                cursor: activeTab === "referral-in" ? "pointer" : "default",
              }}
              onClick={() => {
                // this code is to redirect to provider list page with selected provider name
                if (activeTab === "referral-in") {
                  setSearchStringKey(row?.referralFromName ?? "");
                  setPagination(DEF_PAGINATION);
                  setReferralFilter(DEFAULT_FILTERS);
                  setIsReferral_Provider_list(true);
                  setActiveTab("referral-provider-list");
                }
              }}
            >
              <Box>
                <Text isTabletext color={"#004186"}>
                  {`${row?.referralFromName ?? ""}`}
                </Text>
                {/* <Text  sx={{ fontSize: "14px", color: "#1A1A1A99" }}>
                {`${row?.referralFromPhone ?? ""}`}
              </Text> */}
              </Box>
            </Flex>
          );
        },
      },
      {
        field: "",
        headerName: "Referral To",
        minWidth: 120,
        flex: 0.12,
        renderCell: ({ row }) => {
          return (
            <Flex
              gap={"10px"}
              sx={{
                cursor: activeTab === "referral-out" ? "pointer" : "default",
              }}
              onClick={() => {
                // this code is to redirect to provider list page with selected provider name
                if (activeTab === "referral-out") {
                  setSearchStringKey(row?.referralToName ?? "");
                  setPagination(DEF_PAGINATION);
                  setReferralFilter(DEFAULT_FILTERS);
                  setIsReferral_Provider_list(true);
                  setActiveTab("referral-provider-list");
                }
              }}
            >
              <Box>
                <Text isTabletext color={"#004186"}>
                  {`${row?.referralToName ?? ""}`}
                </Text>
                {/* <Text sx={{ fontSize: "14px", color: "#1A1A1A99" }}>
                {`${row?.referralToPhone ?? ""}`}
              </Text> */}
              </Box>
            </Flex>
          );
        },
      },
      {
        field: "referral_date",
        headerName: "Referral Date",
        minWidth: 140,
        renderCell: ({ row }: any) => (
          <Text isTabletext>{NonNullableDate(row?.referralDate)}</Text>
        ),
      },
      {
        field: "referral_priority",
        headerName: "Priority",
        minWidth: 100,
        renderCell: ({ row }: { row: any }) => {
          const priorityConfig =
            PRIORITY_MAPPING[
              row?.referralPriority as keyof typeof PRIORITY_MAPPING
            ];

          return (
            <Text
              isTabletext
              style={{ color: priorityConfig?.color || "black" }}
            >
              {priorityConfig?.label ?? "-"}
            </Text>
          );
        },
      },
      {
        field: "description",
        headerName: "Response Description",
        minWidth: 170,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "referralStatus",
        headerName: "Response Status",
        minWidth: 160,
        renderCell: ({ row }) =>
          row?.referralStatus ? (
            <StatusChip
              label={replaceUnderScoreAddSpace(
                row?.referralStatus.toLowerCase()
              )}
              variant={
                ReferralStatusVariantMap[row?.referralStatus] ?? "default"
              }
            />
          ) : (
            "-"
          ),
      },
      {
        field: "Action",
        headerName: "Actions",
        minWidth: 120,
        renderCell: ({ row }: any) => {
          const status = row?.referralStatus;
          const isAccepted = status === "ACCEPTED";
          const isRejected = status === "REJECTED" || status === "REJECT";
          return (
            <ActionMenu
              options={
                isRejected
                  ? [{ value: "archive", label: "Archive referral" }]
                  : [
                      { value: "view", label: "View Summary" },
                      ...(isAccepted
                        ? []
                        : [
                            {
                              value: "accept-referral",
                              label: "Accept referral",
                            },
                          ]),
                      { value: "edit", label: "Edit" },
                      { value: "reject-referral", label: "Reject referral" },
                      // { value: "reassign", label: "Reassign" },
                      {
                        value: "archive",
                        label: archive ? "Unarchive" : "Archive" + " referral",
                      },
                      // ...(activeTab === "referral-in"
                      //   ? [{ value: "book-appointment", label: "Book Appointment" }]
                      //   : []),
                      // {
                      //   value: "change-status",
                      //   label: "Change Status",
                      // },
                      // ...(activeTab === "referral-out"
                      //   ? [
                      //       {
                      //         value: "send-through-fax",
                      //         label: "Send through Fax",
                      //       },
                      //     ]
                      //   : []),
                    ]
              }
              onActionClick={(action) => {
                if (action === "edit") {
                  setReferralId(row?.uuid);
                  referralDisclosureHost.open();
                } else if (action == "view")
                  viewReferralDisclosureHost.open(row?.uuid);
                else if (action == "archive") {
                  if (isRejected) {
                    // Rejected referrals: archive directly without confirmation
                    archiveHandler(true, row?.uuid)
                      .then((res) => {
                        isReferral_Provider_list
                          ? refetchProvidersList()
                          : refetch();
                        showSnackbar(getSuccessSnackbar(res));
                      })
                      .catch((err) => showSnackbar(getErrorSnackbar(err)));
                  } else {
                    setArchive(row?.archive);
                    deleteDisc.open(row?.uuid);
                  }
                } else if (action == "change-status") {
                  changeReferralStatusDisclosureHost.open(row?.uuid);
                } else if (action == "book-appointment") {
                  appointmentDisclosure.open({
                    value: row?.patientUuid,
                    label: row?.patientName,
                    birthDate: row?.patientDob,
                  });
                } else if (action == "send-through-fax") {
                  createFaxClaimDisc.open({
                    ...DOCUMENT_INITIAL_VALUE,
                    sCPFromName: row.referralFromName,
                    documentDescription: "Refferal Out Documents",
                  });
                } else if (action == "accept-referral") {
                  handleAcceptReferral(
                    row,
                    AcceptReferralReferralStatusEnum.Accepted
                  );
                } else if (action == "reject-referral") {
                  handleAcceptReferral(
                    row,
                    AcceptReferralReferralStatusEnum.Rejected
                  );
                }
              }}
            />
          );
        },
      },
    ],
    ["patientName", "referral_date", "referral_priority"]
  );

  const providerColumns = applyDefaultColumnSettings<
    UserData & {
      providerType: string;
    }
  >(
    [
      // {
      //   field: "uuid",
      //   flex: 0.3,
      //   headerName: "Provider ID",
      //   sortable: false,
      //   renderCell: ({ row }) => {
      //     return <Text>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>;
      //   },
      // },
      {
        // todo change while backend accept sort key
        field: "providerName",
        flex: 0.4,
        minWidth: 210,
        disableColumnMenu: true,
        headerName: "Name",
        // todo need to fix type
        renderCell: ({ row }: any) => {
          return (
            <Flex gap={0.6} flexDirection={"column"}>
              <Box>
                <Text isTabletext color={"#004186"}>
                  {`${row?.name ?? ""}`}
                </Text>
              </Box>
            </Flex>
          );
        },
        sortable: false,
      },
      {
        field: "referralType",
        flex: 0.2,
        minWidth: 100,
        headerName: "Referral Type",
        sortable: false,
        renderCell: ({ row }: any) => (
          <Text isTabletext>{row?.referralType ?? ""}</Text>
        ),
      },
      {
        field: "npi",
        flex: 0.3,
        minWidth: 170,
        headerName: "NPI Number",
        sortable: false,
        renderCell: ({ row }: any) => (
          <Text isTabletext>{`${row?.npi ?? ""}`}</Text>
        ),
      },
      {
        field: "practiceName",
        flex: 0.4,
        minWidth: 170,
        headerName: "Practice/Facility Name",
        sortable: false,
        renderCell: ({ row }: any) => (
          <Text isTabletext>{`${row?.practiceName ?? ""}`}</Text>
        ),
      },
      {
        field: "contact",
        flex: 0.3,
        minWidth: 140,
        headerName: "Contact Number",
        sortable: false,
      },
      {
        field: "email",
        flex: 0.6,
        minWidth: 240,
        headerName: "Email",
        renderCell: ({ value, row }) => (
          <Flex alignItems={"center"} gap={"4px"}>
            {value ? (
              <>
                <VerifiedIcon
                  color={row?.emailVerified ? "success" : "disabled"}
                  fontSize="small"
                />
                <RenderWithTooltip value={value} />
              </>
            ) : (
              <Text isTabletext>-</Text>
            )}
          </Flex>
        ),
        sortable: false,
      },
      {
        field: "address",
        flex: 0.7,
        minWidth: 150,
        headerName: "Address",
        sortable: false,
        renderCell: ({ row }: any) => (
          <RenderWithTooltip value={row?.address ?? "-"} />
        ),
      },
      {
        field: "Action",
        headerName: "Actions",
        minWidth: 120,
        flex: 0.1,
        renderCell: ({ row }: any) => (
          <ActionMenu
            options={[
              { value: "edit", label: "Edit" },
              {
                value: "archive",
                label: archive ? "Unarchive" : "Archive",
              },
            ]}
            onActionClick={(action) => {
              if (action === "edit") {
                newReferringProvider.open(row?.uuid);
              } else if (action === "archive") {
                deleteDisc.open(row?.uuid);
              }
            }}
          />
        ),
      },
    ],
    ["providerName"]
  );

  const dynamicColumns =
    activeReferral === "provider-centric-tasks"
      ? (COLUMNS?.filter((col) => col.field !== "patientName") as GridColDef[])
      : (COLUMNS as GridColDef[]);

  return (
    <PageLayout
      header={
        <>
          {newReferringProvider.opened && (
            <NewReferringProvider
              open={newReferringProvider.opened}
              onClose={newReferringProvider.close}
              referralInOut={isReferralOutTask ? "Referring" : "Receiving"}
              disclosureHost={newReferringProvider}
            />
          )}
          {createFaxClaimDisc.opened && (
            <UploadFaxDocumentDrawer
              disclosureHost={createFaxClaimDisc}
              allowSecondDocument={false}
            />
          )}
          <ReferralTopBar
            setArchive={setArchive}
            archive={archive}
            filters={referralFilter}
            setFilters={setReferralFilter}
            activeReferral={activeTab}
            setActiveReferral={setActiveTab}
            setSearchParams={setSearchParams}
            setSearchStringKey={setSearchStringKey}
            searchStringKey={instantSearchVal}
            setExpanded={setExpanded}
            expanded={expanded}
            setIsReferral_Provider_list={setIsReferral_Provider_list}
            patientData={patientData}
          />

          <DeleteModal
            w="380px"
            open={deleteDisc.opened}
            onClose={deleteDisc.close}
            id={deleteDisc.deleteId}
            onConfirmation={(id) =>
              archiveHandler(!archive, id).then((res) => {
                isReferral_Provider_list ? refetchProvidersList() : refetch();
                return res;
              })
            }
            content={`You want to ${archive ? "unarchive" : "archive"} this ${
              isReferral_Provider_list ? "Provider" : "Referral"
            }`}
          />

          {referralDisclosureHost.opened && (
            <UpsertReferral
              title="Update Referral"
              disclosureHost={referralDisclosureHost}
              prefillData={{
                ...referralData,
                patient: getObjectFromList(referralData?.patient),
                referralFrom: getObjectFromList(referralData?.referralFrom),
                referralTo: getObjectFromList(referralData?.referralTo),
                referralSpecialities: getObjectFromList(
                  referralData?.referralSpecialities
                ),
                referralDiagnosisCode: transformMultiSelectFrontEndVal(
                  DEFAULT_VALUE_LABEL_LIST,
                  referralData?.referralDiagnosisCode
                ),
                referralDocument: referralData?.referralDocument ?? undefined,
                referralDocumentName:
                  referralData?.referralDocumentName ?? undefined,
                patientInsurance: referralData?.patientInsurance
                  ? {
                      value: referralData?.patientInsurance?.uuid,
                      label:
                        referralData?.patientInsurance?.insurancePayer
                          ?.payerName,
                    }
                  : undefined,
                referralAppointment: getObjectFromList(
                  referralData?.referralAppointment
                ),
              }}
            />
          )}
          {changeReferralStatusDisclosureHost.opened && (
            <ChangeReferralStatus
              open={changeReferralStatusDisclosureHost.opened}
              onClose={changeReferralStatusDisclosureHost.close}
              disclosureHost={changeReferralStatusDisclosureHost}
            />
          )}
          {appointmentDisclosure.opened && (
            <NewAppointment
              prefillPatientData={appointmentDisclosure?.data}
              opened={appointmentDisclosure.opened}
              onClose={appointmentDisclosure.close}
            />
          )}
          {viewReferralDisclosureHost.opened && (
            <ViewReferral disclosureHost={viewReferralDisclosureHost} />
          )}

          {/* <ActiveFilters
            filters={referralFilter ?? {}}
            setFilters={setReferralFilter}
          /> */}
        </>
      }
    >
      <CustomDataGridV2
        loading={isFetching || isFetchingProvidersList}
        key={activeReferral}
        rows={
          isReferral_Provider_list
            ? (providersList?.data?.content ?? [])
            : referral?.data.content || []
        }
        rowCount={
          isReferral_Provider_list
            ? (providersList?.data?.page?.totalElements ?? 10)
            : (referral?.data?.page?.totalElements ?? 10)
        }
        getRowId={(row: any) => row.uuid}
        columns={isReferral_Provider_list ? providerColumns : dynamicColumns}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={
          isReferral_Provider_list
            ? setProviderGridSorting
            : setReferralGridSorting
        }
        sortModel={
          isReferral_Provider_list ? providerGridSorting : referralGridSorting
        }
      />
    </PageLayout>
  );
};

export default Referrals;

const ReferralStatusVariantMap: Record<
  string,
  "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending"
> = {
  COMPLETED: "success",
  ACCEPTED: "success",
  PENDING: "pending",
  REJECTED: "error",
  CANCELLED: "error",
  IN_PROGRESS: "info",
  SCHEDULED: "info",
};
